import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { User, AuthState } from '../types/auth'

// localStorage에서 안전하게 값 가져오기
const getStoredUser = (): User | null => {
  try {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  } catch {
    return null
  }
}

const initialState: AuthState = {
  isAuthenticated: localStorage.getItem('accessToken') !== null,
  user: getStoredUser(),
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // 로그인 성공 시 자격 증명 저장
    setCredentials: (
      state,
      action: PayloadAction<{
        user: User
        accessToken: string
        refreshToken: string
      }>
    ) => {
      const { user, accessToken, refreshToken } = action.payload
      state.isAuthenticated = true
      state.user = user
      state.accessToken = accessToken
      state.refreshToken = refreshToken
      
      localStorage.setItem('accessToken', accessToken)
      localStorage.setItem('refreshToken', refreshToken)
      localStorage.setItem('user', JSON.stringify(user))
    },
    
    // 토큰만 업데이트 (토큰 갱신 시 사용)
    updateTokens: (
      state,
      action: PayloadAction<{
        accessToken: string
        refreshToken: string
      }>
    ) => {
      const { accessToken, refreshToken } = action.payload
      state.accessToken = accessToken
      state.refreshToken = refreshToken
      
      localStorage.setItem('accessToken', accessToken)
      localStorage.setItem('refreshToken', refreshToken)
    },
    
    // 사용자 정보만 업데이트
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
        localStorage.setItem('user', JSON.stringify(state.user))
      }
    },
    
    // 로그아웃
    logout: (state) => {
      state.isAuthenticated = false
      state.user = null
      state.accessToken = null
      state.refreshToken = null
      
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
    },
  },
})

export const { setCredentials, updateTokens, updateUser, logout } = authSlice.actions
export default authSlice.reducer

// 셀렉터
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user
export const selectAccessToken = (state: { auth: AuthState }) => state.auth.accessToken

/**
 * 슈퍼관리자 여부 판별.
 *
 * 우선순위:
 *   1) 백엔드 명시 플래그 user.superAdmin === true (가장 신뢰)
 *   2) 백엔드 mngrSeCd === 'S' (LoginResponse 가 노출, t_mbr_mngr.MNGR_SE_CD)
 *   3) (fallback) 권한명(authrtNm) 패턴 매칭 — backend 가 플래그를 안 보낸 구버전 응답 호환용
 *
 * Why: 권한명 패턴 매칭은 일반 관리자의 권한명에 '시스템관리' 같은 단어가 우연히 포함되면
 *      false positive 가 발생해 슈퍼로 오판된다. 그 결과 로그인 후 첫 접근 가능 메뉴 대신
 *      대시보드로 강제 이동되어 "권한 메뉴 첫 페이지로 안 간다" 라는 사용자 보고가 발생했다.
 *
 * 새 권한명 패턴을 추가해야 하면 SUPER_ADMIN_PATTERNS 만 수정한다 (fallback 만 영향).
 */
const SUPER_ADMIN_PATTERNS = ['슈퍼', '최고관리', '시스템관리']
export function isSuperAdminUser(user: User | null | undefined): boolean {
  if (!user) return false
  // 1) 명시 플래그 우선
  if (user.superAdmin === true) return true
  if (user.mngrSeCd === 'S') return true
  // 2) (구버전 응답) 권한명 패턴 fallback — 명시 플래그가 false 가 아니라 undefined 일 때만 동작
  if (user.superAdmin === undefined && user.mngrSeCd === undefined) {
    const name = user.authrtNm || user.authNm || ''
    if (!name) return false
    return SUPER_ADMIN_PATTERNS.some((p) => name.includes(p))
  }
  return false
}

// 슈퍼관리자 셀렉터
export const selectIsSuperAdmin = (state: { auth: AuthState }) => isSuperAdminUser(state.auth.user)
